/**
 * L6 — Le titre qui mute.
 *
 * Helper qui dérive un libellé court depuis l'état du monde. Utilisé
 * pour muter silencieusement `document.title` au fil du mandat. Le
 * joueur ne reçoit aucune notification — il s'en aperçoit en regardant
 * son onglet de navigateur (ou en partageant un screenshot).
 *
 * C'est le pari viral du sprint Tier 1 (cf. project_sprint_tier1_accessibility).
 * Inspiré de Frostpunk : la dérive morale n'est jamais annoncée, juste
 * révélée *a posteriori*.
 *
 * Critères :
 *   - T0 → T19 : toujours « Moi Président ». Pas de jugement prématuré.
 *   - À partir de T20, le libellé peut basculer dès qu'un seuil est franchi :
 *     * « Moi Dictateur »   — régime non-démocratique ou répression ≥ 0.5
 *     * « Moi Ruiné »       — fin par faillite, ou dette/PIB ≥ 140 %
 *     * « Moi Démago »      — profil politique extrême (un axe > 0.55 absolu)
 *     * « Moi Héros »       — fin de terme + bilan moral majoritairement « sauvé »
 *     * « Moi Président »   — défaut
 *
 * Ordre de priorité ci-dessus — le premier critère vrai gagne, pour éviter
 * qu'un mandat « ruiné mais autoritaire » devienne « démago » par fluctuation.
 */

import type { WorldState } from "./types";

const MUTE_THRESHOLD_TURN = 20;

/** Une promesse est tenue si sa progression atteint 100 % (`progress ≥ 1`). */
export function allPledgesKept(
  pledges: readonly { progress: number }[],
): boolean {
  return pledges.length > 0 && pledges.every((p) => p.progress >= 1.0);
}

/**
 * VICTOIRE (2026-07-23) — découplée de la satisfaction brute (inatteignable,
 * cf. CONSEQUENCES_STUDY). On gagne en **survivant** (mandat mené à terme)
 * et en **tenant sa parole** (toutes les promesses de campagne tenues).
 * Sans promesse, pas de victoire : on n'aura été qu'un gestionnaire.
 */
export function isVictory(
  state: WorldState | null,
  pledges: readonly { progress: number }[],
): boolean {
  if (!state) return false;
  const polity = state.polity;
  return (
    !!polity.mandate_ended &&
    polity.mandate_end_cause === "term" &&
    allPledgesKept(pledges)
  );
}

export function moralRegime(
  state: WorldState | null,
  pledges: readonly { progress: number }[] = [],
): string {
  if (!state) return "Moi Président";

  const turn = state.turn;
  const polity = state.polity;
  const economy = state.economy;

  // Pas de mutation avant T20 — le joueur a besoin d'un peu de marge.
  if (turn < MUTE_THRESHOLD_TURN) return "Moi Président";

  // 1. Dictature — régime non-démocratique ou répression sévère.
  const repression = polity.repression_index ?? 0;
  const isDemocracy = polity.regime === "democracy";
  if (!isDemocracy || repression >= 0.5) return "Moi Dictateur";

  // 2. Ruine — fin par faillite, ou dette explosée.
  const debtRatio = (economy.public_debt / Math.max(economy.gdp, 1)) * 100;
  const cause = polity.mandate_end_cause;
  if (cause === "faillite" || debtRatio >= 140) return "Moi Ruiné";

  // 3. Démago — profil politique extrême sur au moins un axe.
  const p = polity.profile;
  const maxAxis = Math.max(
    Math.abs(p.economic),
    Math.abs(p.societal),
    Math.abs(p.international),
    Math.abs(p.authority),
  );
  if (maxAxis >= 0.55) return "Moi Démago";

  // 4. Héros — VICTOIRE : mandat mené à terme + promesse(s) tenue(s).
  //    Placé APRÈS Dictateur/Ruiné/Démago : être « héros » suppose aussi de
  //    n'avoir sombré ni dans l'autoritarisme, ni la ruine, ni l'extrême.
  if (isVictory(state, pledges)) {
    return "Moi Héros";
  }

  // 5. Défaut.
  return "Moi Président";
}
